import baseColors from 'styles/config/colors/baseColors';
import type { Config } from 'tailwindcss';

const spacing = {
  '0': '0',
  xxs1: '1px',
  xxs2: '2px',
  xs1: '4px',
  xs2: '8px',
  xs3: '12px',
  sm1: '16px',
  sm2: '20px',
  sm3: '24px',
  sm4: '32px',
  md1: '40px',
  md2: '48px',
  md3: '64px',
  lg1: '80px',
  lg2: '120px',
  lg3: '160px',
  xl1: '200px',
  xl2: '240px',
  xl3: '260px',
  xxl1: '300px',
  xxl2: '400px',
  'icon-button': '10px',
} as const;

export const theme = {
  colors: {
    transparent: 'transparent',

    // Color ramp values
    base: {
      white: baseColors.white,
      black: baseColors.black,
      offWhite: baseColors.offWhite,
      grey: baseColors.newGrey,
      brand: baseColors.newBrand,
      red: baseColors.red,
      green: baseColors.green,
      yellow: baseColors.yellow,
      blue: baseColors.blue,
      purple: baseColors.purple,
    },

    // System colors
    'error-text-01': 'var(--tw-red-50)',
    'information-text-01': 'var(--tw-blue-50)',
    standard: 'var(--tw-purple-grey-50)',
    shadow: 'var(--tw-shadow-color)',

    // Text colors
    primary: 'var(--tw-text-primary)',
    'primary-inverse': 'var(--tw-text-primary-inverse)',
    secondary: 'var(--tw-text-secondary)',
    'secondary-inverse': 'var(--tw-text-secondary-inverse)',
    'button-brand': 'var(--tw-text-button-brand)',
    helper: 'var(--tw-text-helper)',
    'helper-inverse': 'var(--tw-text-helper-inverse)',
    placeholder: 'var(--tw-text-placeholder)',
    'placeholder-inverse': 'var(--tw-text-placeholder-inverse)',
    brand: 'var(--tw-text-brand)',
    'brand-inverse': 'var(--tw-text-brand-inverse)',
    accent: 'var(--tw-text-accent)',
    disabled: 'var(--tw-text-disabled)',
    'inverse-disabled': 'var(--tw-text-disabled-inverse)',
    danger: 'var(--tw-text-danger)',
    'danger-inverse': 'var(--tw-text-danger-inverse)',
    success: 'var(--tw-text-success)',
    'success-inverse': 'var(--tw-text-success-inverse)',
    warning: 'var(--tw-text-warning)',
    'warning-inverse': 'var(--tw-text-warning-inverse)',
    info: 'var(--tw-text-info)',

    // Background colors (semantic, theme-driven)
    background: 'var(--tw-background)',
    'background-page': 'var(--tw-background-page)',
    'background-primary': 'var(--tw-background-primary)',
    'background-primary-hover': 'var(--tw-background-primary-hover)',
    'background-primary-inverse': 'var(--tw-background-primary-inverse)',
    'background-primary-inverse-hover':
      'var(--tw-background-primary-inverse-hover)',
    'background-brand': 'var(--tw-background-brand)',
    'background-brand-inactive': 'var(--tw-background-brand-inactive)',
    'background-brand-inverse': 'var(--tw-background-brand-inverse)',
    'background-brand-hover': 'var(--tw-background-brand-hover)',
    'background-accent': 'var(--tw-background-accent)',
    'background-selected': 'var(--tw-background-selected)',
    'background-selected-hover': 'var(--tw-background-selected-hover)',
    'background-accent-inverse': 'var(--tw-background-accent-inverse)',
    'background-inverse': 'var(--tw-background-inverse)',
    'background-inverse-hover': 'var(--tw-background-inverse-hover)',
    'background-inverse-disabled': 'var(--tw-background-inverse-disabled)',
    'background-inverse-skeleton': 'var(--tw-background-inverse-skeleton)',
    'background-hover': 'var(--tw-background-hover)',
    'background-disabled-subtle': 'var(--tw-background-disabled-subtle)',
    'background-disabled-strong': 'var(--tw-background-disabled-strong)',
    'background-skeleton': 'var(--tw-background-skeleton)',
    'background-danger': 'var(--tw-background-danger)',
    'background-danger-hover': 'var(--tw-background-danger-hover)',
    'background-success': 'var(--tw-background-success)',
    'background-success-hover': 'var(--tw-background-success-hover)',
    'background-warning': 'var(--tw-background-warning)',
    'background-warning-hover': 'var(--tw-background-warning-hover)',
    'background-information': 'var(--tw-background-information)',
    'background-information-hover': 'var(--tw-background-information-hover)',
    'background-error': 'var(--tw-background-error)',
    'background-error-hover': 'var(--tw-background-error-hover)',
    'background-overlay': 'var(--tw-background-overlay)',

    // Border colors
    'border-primary': 'var(--tw-border-primary)',
    'border-brand': 'var(--tw-border-brand)',
    'border-brand-hover': 'var(--tw-border-brand-hover)',
    'border-brand-active': 'var(--tw-border-brand-active)',
    'border-brand-inverse': 'var(--tw-border-brand-inverse)',
    'border-brand-inverse-hover': 'var(--tw-border-brand-inverse-hover)',
    'border-brand-inverse-active': 'var(--tw-border-brand-inverse-active)',
    'border-subtle': 'var(--tw-border-subtle)',
    'border-subtle-inverse': 'var(--tw-border-subtle-inverse)',
    'border-table': 'var(--tw-border-table)',
    'border-strong': 'var(--tw-border-strong)',
    'border-strong-inverse': 'var(--tw-border-strong-inverse)',
    'border-underline': 'var(--tw-border-underline)',
    'border-underline-inverse': 'var(--tw-border-underline-inverse)',
    'border-focus': 'var(--tw-border-focus)',
    'border-inverse-focus': 'var(--tw-border-inverse-focus)',
    'border-disabled': 'var(--tw-border-disabled)',
    'border-danger': 'var(--tw-border-danger)',
    'border-success': 'var(--tw-border-success)',
    'border-warning': 'var(--tw-border-warning)',
    'border-info': 'var(--tw-border-info)',

    // Icon colors
    'icon-primary': 'var(--tw-icon-primary)',
    'icon-primary-hover': 'var(--tw-icon-primary-hover)',
    'icon-primary-brand': 'var(--tw-icon-primary-brand)',
    'icon-primary-inverse': 'var(--tw-icon-primary-inverse)',
    'icon-secondary': 'var(--tw-icon-secondary)',
    'icon-secondary-inverse': 'var(--tw-icon-secondary-inverse)',
    'icon-brand': 'var(--tw-icon-brand)',
    'icon-brand-inverse': 'var(--tw-icon-brand-inverse)',
    'icon-accent': 'var(--tw-icon-accent)',
    'icon-button-brand': 'var(--tw-icon-button-brand)',
    'icon-disabled': 'var(--tw-icon-disabled)',
    'icon-inverse-disabled': 'var(--tw-icon-inverse-disabled)',
    'icon-danger': 'var(--tw-icon-danger)',
    'icon-success': 'var(--tw-icon-success)',
    'icon-warning': 'var(--tw-icon-warning)',
    'icon-info': 'var(--tw-icon-info)',

    // Generic surfaces
    'surface-brand-pill': 'var(--tw-surface-brand-pill)',

    // shadcn/ui semantic color keys (mapped via CSS variable aliases)
    shadcn: {
      background: 'var(--background)',
      foreground: 'var(--foreground)',
      card: {
        DEFAULT: 'var(--card)',
        foreground: 'var(--card-foreground)',
      },
      popover: {
        DEFAULT: 'var(--popover)',
        foreground: 'var(--popover-foreground)',
      },
      primary: {
        DEFAULT: 'var(--primary)',
        foreground: 'var(--primary-foreground)',
      },
      secondary: {
        DEFAULT: 'var(--secondary)',
        foreground: 'var(--secondary-foreground)',
      },
      muted: {
        DEFAULT: 'var(--muted)',
        foreground: 'var(--muted-foreground)',
      },
      accent: {
        DEFAULT: 'var(--accent)',
        foreground: 'var(--accent-foreground)',
      },
      destructive: {
        DEFAULT: 'var(--destructive)',
        foreground: 'var(--destructive-foreground)',
      },
      border: 'var(--border)',
      input: 'var(--input)',
      ring: 'var(--ring)',
    },
    sidebar: {
      DEFAULT: 'var(--sidebar-background)',
      foreground: 'var(--sidebar-foreground)',
      primary: {
        DEFAULT: 'var(--sidebar-primary)',
        foreground: 'var(--sidebar-primary-foreground)',
      },
      accent: {
        DEFAULT: 'var(--sidebar-accent)',
        foreground: 'var(--sidebar-accent-foreground)',
      },
      border: 'var(--sidebar-border)',
      ring: 'var(--sidebar-ring)',
    },
  },
  spacing,
  borderWidth: {
    DEFAULT: '1px',
    ...spacing,
  },
  textUnderlineOffset: {
    auto: 'auto',
    ...spacing,
  },
  borderRadius: {
    none: '0',
    xSmall: '4px',
    small: '8px',
    medium: '12px',
    large: '24px',
    xlarge: '32px',
    full: '9999px',
  },
  fontFamily: {
    heading: '"Proxima Nova", sans-serif',
    body: '"Proxima Nova", sans-serif',
    monospace: '"Roboto Mono", monospace',
  },
  fontSize: {
    'header-extra-large': '40px',
    'header-large': '24px',
    'header-medium': '18px',
    'header-regular': '16px',
    'header-small': '12px',
    'body-regular': '14px',
    'body-small': '12px',
    monospace: '12px',
  },
  fontWeight: {
    light: '200',
    normal: '300',
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
  lineHeight: {
    exact: '1',
    tight: '1.2',
    normal: '1.5',
    loose: '2',
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
  },
  screens: {
    xs: '500px',
    sm: '800px',
    md: '1200px',
    lg: '1400px',
  },
  boxShadow: {
    overlay: '0 1px 10px var(--tw-shadow-color)',
    none: '0 0 #000',
  },
  zIndex: {
    tooltip: '1500',
    toast: '1500',
    modal: '1400',
    dropdown: '1450',
    'popup-menu': '1300',
    overlay: '1300',
    'side-panel': '1200',
    'child-sidepanel': '1150',
    navbar: '1100',
    sticky: '900',
  },
  animation: {
    'fade-in': 'fade-in 0.3s ease-in-out',
    'fade-out': 'fade-out 0.3s ease-in-out',
    pulse: 'pulse 1.5s ease-in-out infinite',
    sweep: 'sweep 2s ease-in-out infinite',
    'collapsible-down': 'collapsible-down 0.15s ease-out',
    'collapsible-up': 'collapsible-up 0.15s ease-out',
    glow: 'glow 2s ease-in-out infinite',
    shimmer: 'shimmer 2s linear infinite',
    blink: 'blink 1.5s ease-in-out infinite',
    'breathe-1': 'breathe-1 4s ease-in-out infinite',
    'breathe-2': 'breathe-2 4s ease-in-out 0.5s infinite',
    'breathe-3': 'breathe-3 4s ease-in-out 1s infinite',
    'breathe-4': 'breathe-4 4s ease-in-out 1.5s infinite',
    hourglass: 'hourglass 2s ease-in-out infinite',
    'cursor-blink': 'cursor-blink 1s step-end infinite',
    'new-pulse': 'new-pulse 3.5s ease-in-out infinite',
    'svg-pulse': 'svg-pulse 2s ease-in-out infinite',
  },
  keyframes: {
    'collapsible-down': {
      from: { height: '0' },
      to: { height: 'var(--radix-collapsible-content-height)' },
    },
    'collapsible-up': {
      from: { height: 'var(--radix-collapsible-content-height)' },
      to: { height: '0' },
    },
    'fade-in': {
      from: { opacity: '0' },
      to: { opacity: '1' },
    },
    'fade-out': {
      from: { opacity: '1' },
      to: { opacity: '0' },
    },
    pulse: {
      '0%': {
        transform: 'scale(1)',
      },
      '50%': {
        transform: 'scale(0.8)',
      },
      '100%': {
        transform: 'scale(1)',
      },
    },
    sweep: {
      '0%': {
        transform: 'translateX(-120%)',
      },
      '100%': {
        transform: 'translateX(450%)',
      },
    },
    glow: {
      '0%, 100%': {
        boxShadow: '0 0 8px rgba(99, 102, 241, 0.3)',
      },
      '50%': {
        boxShadow: '0 0 18px rgba(99, 102, 241, 0.5)',
      },
    },
    shimmer: {
      '0%': {
        backgroundPosition: '200% 0',
      },
      '100%': {
        backgroundPosition: '-200% 0',
      },
    },
    blink: {
      '0%, 100%': { backgroundColor: 'var(--tw-border-subtle)' },
      '50%': { backgroundColor: 'var(--tw-text-helper)' },
    },
    'breathe-1': {
      '0%, 100%': { transform: 'translate(0%, 0%) scale(1)' },
      '50%': {
        transform:
          'translate(var(--dx, 5%), var(--dy, -5%)) scale(var(--orb-scale, 1.1))',
      },
    },
    'breathe-2': {
      '0%, 100%': { transform: 'translate(0%, 0%) scale(1)' },
      '50%': {
        transform:
          'translate(var(--dx, -5%), var(--dy, 5%)) scale(var(--orb-scale, 1.1))',
      },
    },
    'breathe-3': {
      '0%, 100%': { transform: 'translate(0%, 0%) scale(1)' },
      '50%': {
        transform:
          'translate(var(--dx, 3%), var(--dy, 3%)) scale(var(--orb-scale, 1.05))',
      },
    },
    'breathe-4': {
      '0%, 100%': { transform: 'translate(0%, 0%) scale(1)' },
      '50%': {
        transform:
          'translate(var(--dx, -3%), var(--dy, -3%)) scale(var(--orb-scale, 1.05))',
      },
    },
    hourglass: {
      '0%': { transform: 'rotate(0deg)' },
      '25%': { transform: 'rotate(90deg)' },
      '50%': { transform: 'rotate(180deg)' },
      '75%': { transform: 'rotate(270deg)' },
      '100%': { transform: 'rotate(360deg)' },
    },
    'cursor-blink': {
      '0%, 100%': { opacity: '1' },
      '50%': { opacity: '0' },
    },
    'new-pulse': {
      '0%': { transform: 'scaleY(0.8) scaleX(0.9)' },
      '55%': { transform: 'scaleY(1.2) scaleX(1.1)' },
      '100%': {
        transform: 'scaleY(1.6) scaleX(1.2)',
        opacity: '0',
        filter: 'blur(5px)',
      },
    },
    'svg-pulse': {
      '0%, 100%': { opacity: '1', transform: 'scale(1)' },
      '50%': { opacity: '0.7', transform: 'scale(0.95)' },
    },
  },
} satisfies Config['theme'];
